import React, { useEffect, useState } from 'react';
import { RadioButton, StatusMessage } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { IconS24Close24 } from '@is24/cosma-ui-icons';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';
import './VerificationLandingPage.css';
import { VerificationMethod } from '../../@types/VerificationMethod';
import VeriffVerification from '../../components/veriffVerification/VeriffVerification';
import FinApiVerification from '../../components/finApiVerification/FinApiVerification';
import WebIdVerification from '../../components/webIdVerification/WebIdVerification';
import { useTvsState } from '../../utils/useTvsState';
import { trackPageView } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useDarkMode } from '../../hooks/useDarkMode';
import InfoPopOver from '../../components/infoPopOver/InfoPopOver';
import veriffLogo from '../../components/veriffVerification/veriff.svg';
import webIdLogo from '../../components/webIdVerification/webid-logo.svg';

interface VerificationLandingPageProps {
  verificationErrorMessage?: string;
  defaultMethod?: VerificationMethod;
  isSchufaClient?: boolean;
  setWebIdPhoneNumber?: (phoneNumber: string | null) => void;
}

const VerificationLandingPage: React.FC<VerificationLandingPageProps> = ({
  verificationErrorMessage,
  defaultMethod,
  isSchufaClient = false,
  setWebIdPhoneNumber = () => {},
}) => {
  const { t } = useTranslation();
  const { clientId, verificationMethods } = useClientConfig();
  const isVerificationMethodProvided = verificationMethods && verificationMethods.length > 0;
  const showVeriff = !isVerificationMethodProvided || verificationMethods.includes(VerificationMethod.Veriff);
  const showFinapi = !isVerificationMethodProvided || verificationMethods.includes(VerificationMethod.Finapi);
  const showWebId = verificationMethods?.includes(VerificationMethod.WebId) ?? false;

  const getInitialMethod = () => {
    if (defaultMethod === VerificationMethod.WebId && showWebId) {
      return VerificationMethod.WebId;
    }
    if (defaultMethod === VerificationMethod.Finapi && showFinapi) {
      return VerificationMethod.Finapi;
    }
    if (showVeriff) {
      return VerificationMethod.Veriff;
    }
    if (showFinapi) {
      return VerificationMethod.Finapi;
    }
    return VerificationMethod.WebId;
  };

  const [method, setMethod] = useState<VerificationMethod>(getInitialMethod());
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [, setTvsState] = useTvsState();
  const [errorMessage, setErrorMessage] = useState('');
  const isDarkModeOn = useDarkMode();
  const showSchufaErrorMessage =
    isSchufaClient && verificationErrorMessage !== undefined && errorMessage === verificationErrorMessage;
  const statusMessageKey = showSchufaErrorMessage ? 'VerificationLandingPage.schufaErrorMessage' : errorMessage;

  useEffect(() => {
    if (!verificationErrorMessage) {
      trackPageView('is24.de.sicherheit.verifizierung.modal', clientId);
    } else {
      setErrorMessage(verificationErrorMessage);
    }
  }, []);

  const renderProviderLabel = (
    titleKey: string,
    subtitleKey: string,
    logoSrc: string,
    logoAlt: string,
    popoverKey: string,
    logoHeight: number,
    logoWidth: number,
  ) => (
    <div className='grid grid-flex'>
      {t(titleKey as any)}
      <div className='font-lightgray font-body-small-light margin-bottom-s'>{t(subtitleKey as any)}</div>
      <div className='grid grid-flex grid-align-center palm-padding-bottom-l'>
        <p className='margin-right-xs font-body-small-regular font-lightgray'>
          {t('VerificationLandingPage.footerText')}
        </p>
        <img src={logoSrc} alt={logoAlt} className='footer-icon' height={logoHeight} width={logoWidth} />
        <InfoPopOver content={t(popoverKey as any, { returnObjects: true })} />
      </div>
    </div>
  );

  return (
    <>
      <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
        <div className='modal-container' style={{ width: 601 }}>
          <div className='modal-title-wrapper'>
            <h4 className='modal-headline'>{t('VerificationLandingPage.heading')}</h4>
            <IconS24Close24
              onClick={() => setTvsState('')}
              data-testid='modal-close-icon'
              className='tvs-close-button'
            />
          </div>
          <div className='modal-content-wrapper display-flex'>
            <div className='modal-content palm-display-flex'>
              {errorMessage && (
                <StatusMessage
                  data-testid='error-message'
                  className='margin-bottom-xxl tvs-status-message'
                  headline={t('VerificationLandingPage.errorHeading')}
                  message={t(statusMessageKey as any)}
                  statusType='error'
                />
              )}
              <div className='grid grid-flex margin-bottom-m margin-top-m'>
                <IdentificationInfoContent isDarkModeOn={isDarkModeOn} />
              </div>
              <div className='grid-item font-heading-xsmall-bold margin-top-m palm-margin-bottom-none'>
                {t('VerificationLandingPage.availableOptions')}
              </div>
              <div className='grid grid-flex margin-top-xxl gutter-horizontal-m margin-top-m palm-margin-bottom-m palm-margin-top-m'>
                {showVeriff && (
                  <div className='grid-item one-whole font-h5'>
                    <RadioButton
                      label={renderProviderLabel(
                        'VerificationLandingPage.VeriffRadioButtonLabel',
                        'VerificationLandingPage.VariffBracketName',
                        veriffLogo,
                        'Veriff',
                        'InformationText.veriffPopOverContent',
                        16,
                        49.48,
                      )}
                      id={VerificationMethod.Veriff}
                      name='verification-method'
                      checked={method === VerificationMethod.Veriff}
                      onChange={(e) => setMethod(e.target.id as VerificationMethod)}
                    />
                  </div>
                )}
                {showFinapi && (
                  <div className='grid-item one-whole grid-justify-center'>
                    <RadioButton
                      label='Online-Banking'
                      id={VerificationMethod.Finapi}
                      name='verification-method'
                      checked={method === VerificationMethod.Finapi}
                      onChange={(e) => setMethod(e.target.id as VerificationMethod)}
                    />
                  </div>
                )}
                {showWebId && (
                  <div className='grid-item one-whole grid-justify-center'>
                    <RadioButton
                      label={renderProviderLabel(
                        'VerificationLandingPage.WebIdRadioButtonLabel',
                        'VerificationLandingPage.WebIDBracketName',
                        webIdLogo,
                        'WebID',
                        'InformationText.webIdPopOverContent',
                        13,
                        49.48,
                      )}
                      id={VerificationMethod.WebId}
                      name='verification-method'
                      checked={method === VerificationMethod.WebId}
                      onChange={(e) => setMethod(e.target.id as VerificationMethod)}
                    />
                  </div>
                )}
              </div>
              {modalContent}
            </div>
          </div>
          {method === VerificationMethod.Veriff ? (
            <VeriffVerification onContentChange={setModalContent} onError={setErrorMessage} />
          ) : method === VerificationMethod.WebId ? (
            <WebIdVerification
              onContentChange={setModalContent}
              onError={setErrorMessage}
              setPhoneNumberForTan={setWebIdPhoneNumber}
            />
          ) : (
            <FinApiVerification
              onContentChange={setModalContent}
              onError={setErrorMessage}
              isDarkModeOn={isDarkModeOn}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default VerificationLandingPage;
